// Cost by product, laid out against a second dimension.
//
// The report groups on ONE dimension, so "each deployment by product" is one
// `group_by=product` read per deployment, and "each tenant by product" one per
// tenant. This turns those reads into a table: one row per read, one column per
// product any row spent on, the same columns in the same order on every row.

import type { UsageCostGroup, UsageCostResponse } from "@crm/shared";

/**
 * `product` is free text on the CRM side: a product the platform adds reaches
 * here before this map knows it, and shows under its own key until it does.
 */
const PRODUCT_LABELS: Readonly<Record<string, string>> = {
  grace: "Grace",
  review_agent: "Review agent",
  internal_agent: "Internal agent",
  unassigned: "Unassigned",
};

/** `unassigned` is spend nobody has placed yet: the worklist, not a product. */
const UNASSIGNED = "unassigned";

export function productLabel(key: string): string {
  return PRODUCT_LABELS[key] ?? key;
}

/** Facts in a group that no meter could price, over every reason. */
export function unpricedFacts(group: UsageCostGroup): number {
  const reasons = group.unpriced_by_reason;
  return reasons.null_field + reasons.rate_unavailable + reasons.division_by_zero;
}

export interface ProductMatrixPart {
  /** What the row is: a deployment slug, or a tenant slug. */
  readonly key: string;
  /** The read's figures, or null while it has none to show. */
  readonly report: UsageCostResponse | null;
}

export interface ProductMatrixRow {
  readonly key: string;
  /** Cost per product. A product the row never spent on is absent, never zero. */
  readonly cells: ReadonlyMap<string, number>;
  readonly totalMinor: number;
  readonly unpricedFacts: number;
}

export interface ProductMatrix {
  /** Every product any row spent on: the costliest first, `unassigned` last. */
  readonly products: readonly string[];
  /** The rows that answered, the costliest first. */
  readonly rows: readonly ProductMatrixRow[];
  readonly totals: ReadonlyMap<string, number>;
  readonly unpricedByProduct: ReadonlyMap<string, number>;
  readonly totalMinor: number;
  /** The currency the reads answered in, or null when none answered. */
  readonly currency: string | null;
}

export function buildProductMatrix(parts: readonly ProductMatrixPart[]): ProductMatrix {
  const totals = new Map<string, number>();
  const unpricedByProduct = new Map<string, number>();
  const rows: ProductMatrixRow[] = [];
  let currency: string | null = null;

  for (const part of parts) {
    if (part.report === null) continue;
    currency ??= part.report.currency;
    const cells = new Map<string, number>();
    let rowUnpriced = 0;
    for (const group of part.report.groups) {
      const unpriced = unpricedFacts(group);
      cells.set(group.key, (cells.get(group.key) ?? 0) + group.cost_minor);
      totals.set(group.key, (totals.get(group.key) ?? 0) + group.cost_minor);
      unpricedByProduct.set(group.key, (unpricedByProduct.get(group.key) ?? 0) + unpriced);
      rowUnpriced += unpriced;
    }
    rows.push({
      key: part.key,
      cells,
      totalMinor: sum(cells.values()),
      unpricedFacts: rowUnpriced,
    });
  }

  const products = [...totals.keys()].sort((left, right) => {
    if (left === UNASSIGNED || right === UNASSIGNED) {
      return Number(left === UNASSIGNED) - Number(right === UNASSIGNED);
    }
    const byCost = (totals.get(right) ?? 0) - (totals.get(left) ?? 0);
    return byCost !== 0 ? byCost : left.localeCompare(right);
  });

  return {
    products,
    rows: rows.sort((left, right) => right.totalMinor - left.totalMinor),
    totals,
    unpricedByProduct,
    totalMinor: sum(totals.values()),
    currency,
  };
}

function sum(values: Iterable<number>): number {
  let total = 0;
  for (const value of values) total += value;
  return total;
}
